/**
 * RWBR — RUNNING A SEASON FROM THE INTERFACE.
 *
 * One call that does what four modules do, in the order they have to happen:
 * the season, then its money, then the winter that settles both. It exists so a
 * button can start a career without the button needing to know that finance
 * reads the season and the off-season reads finance.
 *
 * IT BLOCKS, BUT NOT ALL AT ONCE ANY MORE. Thirty-six race weeks and thirty-six
 * races is a couple of seconds of synchronous work, and there is no honest way
 * to make that instant — the engine is pure and deterministic, which is the
 * whole reason its results can be trusted. What HAS changed is the grain: the
 * season is now run one race at a time through `seasonPlay.ts`'s state machine,
 * so this function yields to the event loop after EVERY RACE and can say
 * "race 14 of 36 — Bristol" rather than "running season 1" for two seconds.
 * `onRace` hands each race over as it lands, which is what lets a screen show a
 * season unfolding instead of a spinner.
 *
 * IT IS STILL THE SAME SEASON. `runSeason` is itself a loop over
 * `runSeasonRace` (there is exactly one season procedure — see `seasonPlay.ts`),
 * so running the races here rather than there changes nothing about the result;
 * `npm run seasonplay` measures that, seed by seed, down to the last Megabuck.
 */

import type { DriverCard } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import { buildField, DEFAULT_ROSTER_SIZE } from '../domain/field.ts';
import {
  beginSeason,
  finishSeason,
  isSeasonComplete,
  runSeasonRace,
  type SeasonRaceResult,
  type SeasonResult,
} from '../domain/season.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import { runOffseason, type OffseasonResult } from '../domain/offseason.ts';
import {
  ownerMap,
  runSeasonFinance,
  seasonFinanceReport,
  teamMap,
  type SeasonFinanceReport,
  type SeasonFinanceResult,
} from '../domain/finance/seasonFinance.ts';
import { profileFromCard } from '../domain/finance/raceFinance.ts';
import { foldMoney, type MoneyLedger } from '../domain/finance/money.ts';
import type { CareerCarry } from '../domain/careerCarry.ts';
import type { SeasonHouseRules } from '../domain/seasonPlay.ts';
import type { HouseRuleOptions } from '../domain/houseRules.ts';

export interface CareerSeason {
  seasonNumber: number;
  seed: number;
  /** The cards the season was RUN with — not the re-rated ones. */
  roster: DriverCard[];
  teams: Team[];
  season: SeasonResult;
  finance: SeasonFinanceResult;
  offseason: OffseasonResult;
  report: SeasonFinanceReport;
  ledger: MoneyLedger;
  /** driverId → the finance team id his money was booked under. */
  teamOf: Record<string, string>;
  /** D-EZ-98 — the sheet switches this season was scored under, if any. */
  houseRules?: SeasonHouseRules;
  /**
   * D-EZ-103 — the realism house rules (D-EZ-83…87) this season was raced under,
   * when any was switched off. Absent = all on, modern era.
   */
  raceRules?: HouseRuleOptions;
}

export interface RunOptions {
  seed: number;
  seasonNumber?: number;
  rosterSize?: number;
  /** Carried forward from the previous winter. Omit to start a new career. */
  roster?: DriverCard[];
  teams?: Team[];
  seasonsRun?: Record<string, number>;
  /**
   * D-EZ-91 / D-EZ-94 — everything else last winter handed on: sponsor
   * contracts, shop banks, drivers' money, live feuds, injuries, suspensions,
   * and any pre-league career priors (D-EZ-97). Omit to start a new career.
   */
  carry?: CareerCarry;
  /** D-EZ-98 — Howard's sheet switches for this season. Off by default. */
  houseRules?: SeasonHouseRules;
  /**
   * Called at every stage boundary AND after every race. `done`/`total` are in
   * stages, not races, so a progress bar built on them still works; the race
   * number is in the stage text ("Race 14 of 36 — Food City 500").
   */
  onProgress?: (stage: string, done: number, total: number) => void;
  /**
   * Handed each race the moment it is over, with the standings as they stood
   * after it. A screen can render a season unfolding off this; nothing it does
   * is read back.
   */
  onRace?: (race: SeasonRaceResult) => void;
}

const breathe = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

export async function runCareerSeason(options: RunOptions): Promise<CareerSeason> {
  const seasonNumber = options.seasonNumber ?? 1;
  const say = options.onProgress ?? (() => {});

  say('Building the field', 0, 4);
  await breathe();

  const field = options.roster && options.teams
    ? { drivers: options.roster.map((card) => ({ card })), teams: options.teams }
    : buildField(options.seed, { size: options.rosterSize ?? DEFAULT_ROSTER_SIZE });

  const roster: DriverCard[] = options.roster ?? field.drivers.map((d) => d.card);
  const teams: Team[] = options.teams ?? (field.teams as Team[]);

  say(`Running season ${seasonNumber} — thirty-six race weeks`, 1, 4);
  await breathe();
  // One race at a time, so the interface can watch. `beginSeason` +
  // `runSeasonRace` × 36 + `finishSeason` IS `runSeason` (see `season.ts`) —
  // this is the same call spread over thirty-six turns of the event loop.
  let state = beginSeason({
    seed: options.seed,
    roster,
    teams: teams.map((t) => ({ teamId: t.teamId, name: t.name, driverIds: t.driverIds })),
    ...(options.carry ? { inherited: options.carry } : {}),
    ...(options.houseRules ? { options: { houseRules: options.houseRules } } : {}),
  });
  while (!isSeasonComplete(state)) {
    const step = runSeasonRace(state);
    state = step.state;
    options.onRace?.(step.result);
    say(
      `Race ${step.result.raceNumber} of ${SEASON_RACES} — ${step.result.scheduled.raceName}`,
      1,
      4,
    );
    // A race is 15-50 ms of arithmetic. Yielding after each one keeps the tab
    // alive and costs one macrotask per race; yielding INSIDE a race is not
    // possible without making the engine async, which would cost determinism.
    await breathe();
  }
  const season = finishSeason(state);

  say('Settling the books', 2, 4);
  await breathe();
  const teamOf = teamMap(roster);
  const finance = runSeasonFinance({
    season,
    roster,
    teamOf,
    options: { ownerOf: ownerMap(teams) },
    ...(options.carry ? { carry: options.carry.finance } : {}),
  });

  say('The winter — re-rating, demotions, rookies', 3, 4);
  await breathe();
  const offseason = runOffseason({
    seed: options.seed,
    season,
    roster,
    teams,
    money: finance.events,
    financeTeamOf: teamOf,
    seasonsRun: options.seasonsRun,
    options: { seasonNumber },
    financeCarry: finance.carry,
    priors: options.carry?.priors,
  });

  const profiles = roster.map((c) => profileFromCard(c, teamOf[c.driverId]));
  const report = seasonFinanceReport(finance, profiles);

  say('Done', 4, 4);
  return {
    seasonNumber,
    seed: options.seed,
    roster,
    teams,
    season,
    finance,
    offseason,
    report,
    ledger: foldMoney(finance.events),
    teamOf,
    ...(options.houseRules ? { houseRules: options.houseRules } : {}),
  };
}

/** The next season, from this one's winter. */
export function nextSeasonOptions(previous: CareerSeason, seed: number): RunOptions {
  return {
    seed,
    seasonNumber: previous.seasonNumber + 1,
    roster: previous.offseason.roster,
    teams: previous.offseason.teams,
    seasonsRun: previous.offseason.seasonsRun,
    carry: previous.offseason.carry,
    // A league keeps its house rules from one season to the next.
    ...(previous.houseRules ? { houseRules: previous.houseRules } : {}),
  };
}
